import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type CookieCategory = 'necessary' | 'functional' | 'analytics' | 'marketing';

export interface CookieConsentState {
  version: number; // bump to re-ask
  updatedAt: string;
  categories: Record<CookieCategory, boolean>;
}

const CONSENT_STORAGE_KEY = 'cookie-consent';
const CONSENT_VERSION = 1;

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private _platformId = inject(PLATFORM_ID);
  
  constructor() {}

  private get isBrowser(): boolean {
    return isPlatformBrowser(this._platformId);
  }

  getConsent(): CookieConsentState | null {
    if (!this.isBrowser) return null;
    try {
      const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!raw) return null;
      const parsed: CookieConsentState = JSON.parse(raw);
      if (!parsed || parsed.version !== CONSENT_VERSION) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  hasMadeChoice(): boolean {
    return !!this.getConsent();
  }

  acceptAll(): void {
    this.save({ necessary: true, functional: true, analytics: true, marketing: true });
  }

  acceptOnlyNecessary(): void {
    this.save({ necessary: true, functional: false, analytics: false, marketing: false });
  }

  save(categories: Record<CookieCategory, boolean>): void {
    if (!this.isBrowser) return;
    
    const state: CookieConsentState = {
      version: CONSENT_VERSION,
      updatedAt: new Date().toISOString(),
      categories,
    };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
  }

  // Helper getters for feature toggling
  isEnabled(category: CookieCategory): boolean {
    const consent = this.getConsent();
    return !!consent?.categories[category];
  }
}


